import {
  useIsCollectionsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { replaceSourceNameForTenantIfRequired } from '@modules/common/utils';
import { NoAudioSourceBanner } from '@modules/feedback/components/AudioType/NoAudioSourceBanner';
import CopyFeedbackURLToClipboard from '@modules/feedback/components/FeedbackCard/CopyFeedbackURLToClipboard';
import { SaveFeedback } from '@modules/feedback/components/FeedbackCard/SaveFeedback';
import {
  isFeedbackSummaryPresent,
  isFeedbackSummarySupported,
} from '@modules/feedback/components/FeedbackSummary/utils';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { useIsFeedbackOnFeedbackV2Enabled } from '@modules/feedback/hooks';
import { InfoOutlined, InfoRounded } from '@mui/icons-material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { IntegrationChannelType } from 'generated/graphql';
import { useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Feedback } from 'v2/core/entities/feedback';
import { trackFeedbackEvents } from '../../utils';
import SourceIcon from './FeedbackSourceIcon';

export const trackFeedbackOnFeedbackEvents =
  createAnalyticsTracker('feedbackOnFeedback');

interface FeedbackCardHeaderProps {
  feedback: Feedback;
  onViewDetailsButtonClick: () => void;
  variant: 'compact' | 'default';
  isSummaryView: boolean;
  feedbackListViewType: FeedbackListViewType;
  shouldShowRemoveFromCollectionButton?: boolean;
}

const FeedbackCardHeader = ({
  feedback,
  onViewDetailsButtonClick,
  isSummaryView = true,
  feedbackListViewType,
  shouldShowRemoveFromCollectionButton = false,
}: FeedbackCardHeaderProps) => {
  const { isFeedbackOnFeedbackV2Enabled } = useIsFeedbackOnFeedbackV2Enabled();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isCollectionEnabled = useIsCollectionsEnabled();
  const { id: feedbackID, sourceV2, sourceCreatedAt, namespace } = feedback;

  const shouldShowAllActions =
    feedbackListViewType !== FeedbackListViewType.SAMPLE;

  const externalLinkNode = () => {
    if (!shouldShowAllActions) {
      return null;
    }

    return (
      <Tooltip
        title={
          !!sourceV2?.externalLink
            ? 'Click to go to source'
            : 'Source link not available'
        }
        placement="top"
        arrow
      >
        <Box component="span">
          <IconButton
            aria-label="Go to source"
            href={!!sourceV2?.externalLink ? sourceV2.externalLink : ''}
            disabled={!sourceV2?.externalLink}
            target="_blank"
            rel="noreferrer"
            size="small"
            onClick={() => {
              trackFeedbackEvents({
                eventName: 'Feedback External Source Link Clicked',
                feedbackID,
                sourceCreatedAt,
                sourceName: sourceV2?.name,
              });
            }}
          >
            <OpenInNewRoundedIcon />
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  const getNamespacedSourceName = (sourceName: string) => {
    return namespace ? `${sourceName} ${namespace}`.trim() : sourceName;
  };

  const getSourceName = () => {
    /* For splitting "ZENDESK_SUPPORT" to "ZENDESK SUPPORT" */
    const sourceName =
      shouldRemoveFileUploadPrefix &&
      sourceV2?.integrationChannelType === IntegrationChannelType.FileUpload
        ? ''
        : sourceV2?.name ?? '';
    return replaceSourceNameForTenantIfRequired(
      getNamespacedSourceName(sourceName.trim()),
    );
  };

  const [isNoSummaryCopyExpanded, setIsNoSummaryCopyExpanded] = useState(false);
  const isSummarySupported = isFeedbackSummarySupported(feedback);
  const hasFeedbackSummary = isFeedbackSummaryPresent(feedback);

  return (
    <Stack
      p={2}
      direction={'row'}
      spacing={2}
      alignItems={'center'}
      justifyContent={'space-between'}
    >
      <Box
        sx={{
          position: 'relative',
          opacity: 1,
        }}
      >
        <Stack spacing={2} direction="row" alignItems="center">
          <SourceIcon sourceV2={sourceV2} />
          <Typography variant="subtitle2">{getSourceName()}</Typography>
          {externalLinkNode()}
          {isSummarySupported &&
            !hasFeedbackSummary &&
            isSummaryView &&
            !isFeedbackOnFeedbackV2Enabled && (
              <Box
                onMouseEnter={() => setIsNoSummaryCopyExpanded(true)}
                onMouseLeave={() => setIsNoSummaryCopyExpanded(false)}
              >
                <Box
                  sx={{
                    borderRadius: 1,
                    py: 0.5,
                    px: 1,
                    backgroundColor: (theme) =>
                      alpha(theme.palette.text.primary, 0.05),
                  }}
                >
                  <Stack spacing={1} direction="row" alignItems="center">
                    {isNoSummaryCopyExpanded ? (
                      <InfoRounded
                        sx={{ color: 'text.secondary' }}
                        fontSize="small"
                      />
                    ) : (
                      <InfoOutlined
                        sx={{ color: 'text.secondary' }}
                        fontSize="small"
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {isNoSummaryCopyExpanded
                        ? 'Feedback not summarized for short, or insufficient content'
                        : 'Feedback not summarized'}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            )}
        </Stack>
      </Box>
      <NoAudioSourceBanner feedback={feedback} />
      {shouldShowAllActions ? (
        <Stack spacing={2} direction="row" alignItems="center">
          <SaveFeedback
            feedbackID={feedback.id}
            shouldShowRemoveFromCollectionButton={
              shouldShowRemoveFromCollectionButton
            }
          />
          {!isCollectionEnabled && (
            <CopyFeedbackURLToClipboard
              isDetailView={false}
              feedbackID={feedback.id}
            />
          )}
          <Button
            aria-label="View details"
            onClick={() => {
              onViewDetailsButtonClick();
              trackFeedbackEvents({
                eventName: 'Feedback Card Expanded',
                feedbackID,
              });
            }}
          >
            <OpenInFullIcon />
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2} direction="row" alignItems="center">
          <CopyFeedbackURLToClipboard
            isDetailView={false}
            feedbackID={feedback.id}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default FeedbackCardHeader;
